import {
    Directive,
    ElementRef,
    Renderer2,
    NgZone,
    Input,
    OnInit,
    OnDestroy,
    Output,
    EventEmitter,
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LocalStorageService } from '../services/local-storage.service';

/**
 * A DIRECTIVE THAT CAN BE ADDED TO ANGULAR MATERIAL TABLE TO ALLOW COLUMN RESIZING
 */
@Directive({
    selector: '[appColumnResize]',
    standalone: false
})
export class ColumnResizeDirective implements OnInit, OnDestroy {

    @Output() columnDoubleClick = new EventEmitter<void>();
    private tableWidth: number = 0;

    /**
     * The name with which we store the widths in LOCALSTORAGE
     * 'Name'-column-width
     */
    @Input() localStorageName: string ="default";
    @Input() amountOfColumnsShown: number = 1;
    @Input() colSpan: number = 1;
    @Input() mobileUse: boolean = true;
    /**
     * MINIMAL WIDTH, no Column should be resized smaller than this.
     */
    private MIN_WIDTH = 65;

    // STYLES AS PROPERTIES:
    private normal_BorderStyling = '1px solid var(--tablecontentborder)';
    private no_BorderStyling = '0px';
    private hover_BorderStyling = '1px dotted var(--tablecontentborder)';
    private active_BorderStyling = '1px solid var(--tablecontentborder)';

    /**
     * when ACTIVELY dragging, we should not print some borders, neither on hover oud stuff.
     * We keep this as a property
     */
    private isResizing = false;
    /**
     * when HOVERING, we should print a border, this retains the property for that it should be removed when exiting.
     */
    private hasDrawnAttentiveBorder = false;

    // NEEDED FOR DRAGGING:
    private startX!: number;
    private startWidth!: number;
    private column: HTMLElement;
    private resizer!: HTMLElement;
    private destroy$ = new Subject<void>();


    constructor(private el: ElementRef, private renderer: Renderer2, private zone: NgZone) {
        // we store the element WITH the directive as the resizing column.
        this.column = this.el.nativeElement;
    }
    /**
     * (Creates) a consistent name for localStorage
     * @returns name of what the property is stored under in LocalStorage
     */

    ngOnInit() {
        // draw the drag drop thingie:
        this.createResizer();
        if(!this.mobileUse){
            this.amountOfColumnsShown = this.amountOfColumnsShown-1;
            this.setColumnWidthForDesktop(this.column.id !== "requestId");
        } else {
            this.amountOfColumnsShown = this.amountOfColumnsShown;
            this.setColumnWidthForMobile();
        }
        if(this.column.id === "requestId"){
            return;
        }
        // listen to drag events: down,up,move
        this.initializeResizeListener();
    }
    /**
     * Create the element that can be dragged/displays cursus thingie:
     */
    private createResizer() {
        this.resizer = this.renderer.createElement('div');  
        if(this.column.id !== "requestId"){
            this.resizer.addEventListener('dblclick', (event) => { event.stopPropagation(); event.preventDefault(); this.columnDoubleClick.emit(); });
            this.resizer.addEventListener('click', (event) => { event.stopPropagation(); event.preventDefault() });
            this.resizer.addEventListener('mouseover', () => { this.drawHoverBorder(); });
            this.resizer.addEventListener('mouseout', () => { this.removeHoverBorder(); });
            this.renderer.addClass(this.resizer, 'column-resizer');
            this.renderer.setStyle(this.resizer, 'cursor', 'col-resize');
        }
        this.renderer.setStyle(this.resizer, 'position', 'absolute');
        this.renderer.setStyle(this.resizer, 'right', '0');
        this.renderer.setStyle(this.resizer, 'top', '0');
        this.renderer.setStyle(this.resizer, 'width', '10px');
        this.renderer.setStyle(this.resizer, 'height', '100%');
        this.drawBorderForHeaderColumn(this.normal_BorderStyling);
        this.renderer.appendChild(this.column, this.resizer);
    }
    /**
     * LISTENERS for mouse click, move and up.
     * Listeners for drag EVENTS
     */
    private initializeResizeListener() {
        this.zone.runOutsideAngular(() => {
            fromEvent(this.resizer, 'mousedown')
                .pipe(takeUntil(this.destroy$))
                .subscribe((event) => this.onMouseDown(event));

            fromEvent(document, 'mousemove')
                .pipe(takeUntil(this.destroy$))
                .subscribe((event) => this.onMouseMove(event));

            fromEvent(document, 'mouseup')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.onMouseUp());
        });
    }
    /**
     * EVENT for when the column-resizer element is clicked:
     * activates a border, and STARTS resizing
     */
    private onMouseDown(event: any) {
        event.preventDefault();
        this.isResizing = true;
        this.startX = event.pageX;
        this.startWidth = this.column.offsetWidth;
        this.drawBorderForHeaderColumn(this.active_BorderStyling); // CLICKED border
        this.drawBorderForAllColumnRows(this.active_BorderStyling); // CLICKED border
    }

    /**
     * EVENT for when the mouse is moved
     * Only acts after Resizing is ACTIVATED
     * Changes Width and keeps it stored in LocalStorage
     */
    private onMouseMove(event: any) {
        if (!this.isResizing) return;
        var width = this.startWidth + (event.pageX - this.startX);
        if (width < this.MIN_WIDTH) {
            width = this.MIN_WIDTH
        }

        localStorage.setItem(this.localStorageName, width.toString());
        this.renderer.setStyle(this.column, 'width', `${width}px`);
    }

    /**
     * EVENT for when the column-resizer element is LET GO:
     * de-activates a border, and STOPS resizing
     */
    private onMouseUp() {
        if (!this.isResizing) return;
        this.isResizing = false;
        this.drawBorderForHeaderColumn(this.normal_BorderStyling); // reset border
        this.drawBorderForAllColumnRows(this.no_BorderStyling); // remove border
    }
    /**
     * Draws a HOVER Border, for the HEADER and all rows below it.
     * Does nothing when resizing, or already drawn hover border
     */
    private drawHoverBorder() {
        if (this.hasDrawnAttentiveBorder || this.isResizing) {
            this.hasDrawnAttentiveBorder = true;
            return;
        }
        this.hasDrawnAttentiveBorder = true;
        this.drawBorderForHeaderColumn(this.hover_BorderStyling); // hover border
        this.drawBorderForAllColumnRows(this.hover_BorderStyling); // hover border
    }
    /**
     * Removes the HOVER Border, for the HEADER and all rows below it.
     * Does nothing when resizing, or NO drawn hover border
     */
    private removeHoverBorder() {
        if (!this.hasDrawnAttentiveBorder || this.isResizing) {
            this.hasDrawnAttentiveBorder = false;
            return;
        }
        this.hasDrawnAttentiveBorder = false;
        this.drawBorderForHeaderColumn(this.normal_BorderStyling); // reset border
        this.drawBorderForAllColumnRows(this.no_BorderStyling); // remove border
    }
    /**
     * Adds the given Border Style as Border-Right
     */
    private drawBorderForHeaderColumn(borderStyle: string) {
        this.renderer.setStyle(this.column, 'border-right', borderStyle);
    }
    /**
     * Adds the given Border Style as Border-Right to all Columns Below the Resize header
     */
    private drawBorderForAllColumnRows(borderStyle: string) {
        let columns = Array.from(document.getElementsByClassName(this.column!.id) as HTMLCollectionOf<HTMLElement>);
        columns = columns.concat(Array.from(document.getElementsByClassName(this.column!.id + "_secondRow") as HTMLCollectionOf<HTMLElement>));
        if (this.column!.id === "requestName") {
            columns = columns.concat(Array.from(document.getElementsByClassName("actionRequired") as HTMLCollectionOf<HTMLElement>));
        }
        columns.forEach(foundColumn => {
            this.renderer.setStyle(foundColumn, 'border-right', borderStyle);
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Sets the column width based on localStorage or calculates it dynamically.
     * @param useLocalStorage - Whether to use the stored width from localStorage or calculate a new one.
     */
    public setColumnWidthForDesktop(useLocalStorage: boolean = true) {
        if (useLocalStorage) {
            this.setWidthFromLocalStorage();
        } else {
            this.calculateAndSetWidth();
        }
    }

    /**
     * Sets the column width using the value stored in localStorage.
     * If no value exists, calculates and stores the current width.
     */
    private setWidthFromLocalStorage() {
        const existingWidth = localStorage.getItem(this.localStorageName);

        if (existingWidth) {
            // Apply the stored width
            this.renderer.setStyle(this.column, 'width', `${existingWidth}px`);
        } else {
            // Calculate and store the current width if no stored width exists
            let currentWidth = this.column.getBoundingClientRect().width;

            if (currentWidth < this.MIN_WIDTH) {
                this.calculateTableWidthIfNeeded();
                currentWidth = this.tableWidth / (this.amountOfColumnsShown-1) * this.colSpan;
            }

            localStorage.setItem(this.localStorageName, currentWidth.toString());
            this.renderer.setStyle(this.column, 'width', `${currentWidth}px`);
        }
    }

    /**
     * Calculates and sets the column width dynamically without using localStorage.
     */
    private calculateAndSetWidth() {
        let currentWidth = 0;
        if(this.column.id === 'requestId'){
            currentWidth = 40;
        } else {
            this.calculateTableWidthIfNeeded();
            currentWidth = (this.tableWidth / this.amountOfColumnsShown) * this.colSpan;
        }
        localStorage.setItem(this.localStorageName, currentWidth.toString());
        this.renderer.setStyle(this.column, 'width', `${currentWidth}px`);
    }

    /**
     * Calculates the table width if it hasn't been calculated yet.
     */
    private calculateTableWidthIfNeeded() {
        this.tableWidth = (document.getElementsByClassName('resizable-table')?.[0].getBoundingClientRect().width || 0) - 82;
    }

    private setColumnWidthForMobile() {
        this.calculateAndSetWidth();
    }
}